// src/pages/stream-management-grid/components/StreamGrid.tsx
import React, { useEffect, useMemo, useState } from 'react';
import StreamCard from './StreamCard';
import Icon from '../../../components/AppIcon';
import { mediamtxService } from '@/mediaservices/mediamtxService';

type MuteState = {
  selfMuted?: boolean;
  adminMuted?: boolean;
  effectivelyMuted?: boolean;
};

type CardItem = {
  id: string | number;
  deviceName: string;
  device_name?: string;
  location?: string;
  deviceType?: 'camera' | 'phone' | string;
  status?: 'online' | 'ready' | 'connecting' | 'offline' | string;
  whepUrl?: string | null;          // MediaMTX WHEP
  mediaStream?: MediaStream | null; // Mediasoup (future)
  muteState?: MuteState;
  videoOff?: boolean;
};

type Props = {
  items?: CardItem[];
  onStreamAction?: (id: string | number, action: string) => void;
  onDeviceReload?: (deviceSerial: string) => void;
  loading?: boolean;
};

const StreamGrid: React.FC<Props> = ({ items = [], onStreamAction, onDeviceReload, loading }) => {

  const list = items; // Keep the exact API order

  const handleStreamAction = (id: string | number, action: string) => {
    onStreamAction?.(id, action);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {Array.from({ length: 12 }).map((_, index) => (
          <div key={index} className="bg-card border border-border rounded-lg overflow-hidden animate-pulse">
            <div className="aspect-video bg-muted" />
            <div className="p-4 space-y-3">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/2" />
              <div className="flex justify-between">
                <div className="h-3 bg-muted rounded w-1/4" />
                <div className="h-3 bg-muted rounded w-1/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!list.length) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
          <Icon name="Video" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No streams found</h3>
        <p className="text-muted-foreground">No devices with streams are available right now.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
      {list.map((item) => (
        <StreamCard
          key={item.id}
          stream={{
            id: item.id,
            deviceName: item.deviceName,
            device_name: item.device_name,
            location: item.location,
            deviceType: item.deviceType,
            status: item.status,
            whepUrl: item.whepUrl,
            mediaStream: item.mediaStream,
            muteState: item.muteState,
            videoOff: item.videoOff,
          }}
          onStreamAction={handleStreamAction}
          onDeviceReload={onDeviceReload}
        />
      ))}
    </div>
  );
};

export default StreamGrid;
